import { CameraConfig, ModelBasedVisual } from "../../../visuals/ModelBasedVisual";
import { CameraRig, WorldRenderer } from "../../WorldRenderer"
import { WREvents } from "../../WREvents";
import { BaseVisual } from "../../../visuals/BaseVisual";
import { useEffect, useRef, useState } from "react";
import { CameraChannel, DEFAULT_FRAME_SIZE } from "../../../api/robocore/CameraChannel";
import * as THREE from "three";
import { CameraOverlayDataComposer } from "./CameraOverlayDataComposer";
import { Robot } from "../../../models/Robot";
import { RobotAgent } from "../robot/RobotAgent";
import { FPVCamera } from "../FpvCameraService";
import { set } from "ace-builds-internal/config";

let _renderer: THREE.WebGLRenderer;
let _cameraVisual: BaseVisual;
let _cameraIndex: number;
export function CameraOverlay({ wr, size, position }: {
  wr: WorldRenderer,
  size: { width: number, height: number },
  position: { x: number, y: number }
}) {
  const divRef = useRef<HTMLDivElement>(null);
  const divCanvas = useRef<HTMLDivElement>(null);
  const [isMaximized, setIsMaximized] = useState(false);
  const [selectedCamera, setSelectedCamera] = useState<CameraConfig | null>(null);
  const [simStreamingAgent, setSimStreamingAgent] = useState<RobotAgent | null>(null);
  const [overlayData, setOverlayData] = useState<any>(null);
  useEffect(() => {
    const visualLoadedListener = async (visual: BaseVisual) => {
    };
    const visualRemovedListener = async (visual: BaseVisual) => {
    };
    wr.onEvent(WREvents.VisualLoaded, visualLoadedListener);
    wr.onEvent(WREvents.VisualRemoved, visualRemovedListener);
    const cameraSelectedListener = (visual: ModelBasedVisual, index: number) => {
      // console.log('camera select fired');
      setOverlayData(null);
      const div = divRef.current!;
      div.style.display = "none";
      wr.rig = null;
      if (_renderer) {
        _renderer.dispose();
        _renderer.forceContextLoss();
        // _renderer.context = null as any;
        _renderer.domElement = null as any;
        _renderer = null as any;
      }
      while (div.firstChild) div.removeChild(div.firstChild);
      if (!visual) {
        if (simStreamingAgent) {
          const cameraChannel = simStreamingAgent.connection.channels.find(
            (c) => c.getConfiguration().name === "camera"
          ) as CameraChannel;
          if (cameraChannel) {
            cameraChannel.disconnectFromCamera();
          }
        }
        setSimStreamingAgent(null);
        setSelectedCamera(null);
      } else {
        div.style.display = "block";
        const config = visual.getCameras()[index];
        setSelectedCamera(config);
        _cameraVisual = visual;
        _cameraIndex = index;
        const fov = config.fov || 60; // Default to 60 degrees if not specified
        const camera = new THREE.PerspectiveCamera(fov, div.clientWidth / div.clientHeight, 0.1, 200000);
        const parent = config.parent;
        if (config.gimbal) {
          const gimbalObj = new THREE.Object3D();
          gimbalObj.rotation.copy(config.gimbal);
          parent.add(gimbalObj);
          gimbalObj.add(camera);
        } else {
          parent.add(camera);
        }
        camera.position.set(config.position.x, config.position.y, config.position.z);
        camera.rotation.set(config.rotation.x, config.rotation.y, config.rotation.z);
        _renderer = new THREE.WebGLRenderer({ antialias: true });
        _renderer.setPixelRatio(window.devicePixelRatio);
        _renderer.setSize(div.clientWidth, div.clientHeight);
        // debugger
        div.appendChild(_renderer.domElement);
        wr.rig = { camera, renderer: _renderer };
        const agent = visual.agent as RobotAgent;
        if (agent) {
          const cameraChannel = agent.connection.channels.find(
            (c) => c.getConfiguration().name === "camera"
          ) as CameraChannel
          if (cameraChannel) {
            setSimStreamingAgent(agent);
            // console.log("FPV Connected to camera channel:", cameraChannel);
            const fpv: FPVCamera = {
              agent,
              renderer: _renderer,
              camera: camera,
              div,
              object: visual.obj,
              scene: wr.scene
            };
            cameraChannel.connectToCamera(fpv);
            // setFrameSize(cameraChannel.frameSize);
          } else {
            console.error("Camera channel not found for actor:", agent);
          }
        }
      }
    };
    wr.onEvent(WREvents.CameraSelected, cameraSelectedListener);
    const overlayUpdateListener = (visual: BaseVisual, index: number, d: any) => {
      // console.log({ d });
      if (visual._id === _cameraVisual?._id && _cameraIndex == index) {
        if (d.template) {

        } else {
          setOverlayData({ ...d });
        }
      } else {
        console.log(`no match for index and visual ${visual._id} != ${_cameraVisual?._id} or ${_cameraIndex} != ${index}`)
      }
    }
    wr.onEvent(WREvents.CameraOverlayUpdate, overlayUpdateListener);
    // console.log(Array.from(wr.dataService.visuals.values()));
    for (const visual of wr.dataService.visuals.values()) {
      visualLoadedListener(visual);
    }
    return () => {
      wr.off(WREvents.VisualLoaded, visualLoadedListener);
      wr.off(WREvents.VisualRemoved, visualRemovedListener);
      wr.off(WREvents.CameraSelected, cameraSelectedListener);
      wr.off(WREvents.CameraOverlayUpdate, overlayUpdateListener);
    };
  }, [wr]);
  let width = DEFAULT_FRAME_SIZE[0] / 2;
  let height = DEFAULT_FRAME_SIZE[1] / 2;
  if (isMaximized) {
    width = DEFAULT_FRAME_SIZE[0];
    height = DEFAULT_FRAME_SIZE[1];
  }
  // console.log(selectedCamera);
  return (
    <div
      style={{
        position: "absolute",
        top: `${position.y}px`,
        left: `${position.x}px`,
        width: `${size.width}px`,
        height: `${size.height}px`,
        pointerEvents: "none",
        backgroundColor: 'transparent',
      }}
    >
      <div ref={divRef} style={{
        border: "1px solid green",
        position: "relative",
        left: `${size.width - width}px`,
        top: `${size.height - height - 115}px`,
        width: `${width}px`,
        height: `${height}px`,
        display: "none"
      }}>
      </div>
      <div ref={divCanvas} style={{
        position: "absolute",
        left: `${size.width - width}px`,
        top: `${size.height - height - 115}px`,
        width: `${width}px`,
        height: `${height}px`,
        pointerEvents: 'none',
        zIndex: 2
      }}>
        {overlayData && <CameraOverlayDataComposer data={overlayData} />}
      </div>

    </div>
  );
}
